import { FormControl, TextField } from '@mui/material';
import { DateValidationError } from '@mui/x-date-pickers/models';

import { CoreBox, CoreTypography } from '@youscience/core';
import { brightpathTheme } from '@youscience/theme';

import CoreDatePicker from '@components/CoreDatePicker';

interface MonthYearDatePickerProps {
  name: string;
  label: string;
  value: string | undefined;
  error?: string;
  minDate?: Date;
  maxDate?: Date;
  required?: boolean;
  slotProps?: {
    inputProps?: {
      clearable?: boolean;
      onClear?: () => void;
    };
    error?: boolean;
  };
  handleDateChange: (date: Date | null) => void;
  handleDateError?: (name: string, error: string | undefined) => void;
}

export const MonthYearDatePicker: React.FC<MonthYearDatePickerProps> = ({
  name,
  label,
  value,
  error,
  minDate,
  maxDate,
  required,
  slotProps,
  handleDateChange,
  handleDateError,
}: MonthYearDatePickerProps) => {
  const getDate = (value: string | undefined) => {
    if (!value) {
      return null;
    }

    try {
      // Handle both formats: "12/2024" and "December 2024"
      if (value.includes('/')) {
        const [month, year] = value.split('/');
        return new Date(parseInt(year, 10), parseInt(month, 10) - 1, 1);
      } else {
        const [monthName, year] = value.split(' ');
        const date = new Date(`${monthName} 1, ${year}`);
        if (isNaN(date.getTime())) {
          return null;
        }
        return date;
      }
    } catch {
      return null;
    }
  };

  const showHelperText = () => {
    const isError = error ? { color: `${brightpathTheme.palette.error.main}` } : {};

    return (
      <CoreTypography variant="caption" sx={isError}>
        {error ? error : 'Date uses ‘month year’ format(e.g. August, 2020)'}
      </CoreTypography>
    );
  };

  return (
    <FormControl fullWidth data-testid={name}>
      <CoreDatePicker
        views={['month', 'year']}
        label={`${label} ${required ? '*' : ''}`}
        slots={{
          textField: TextField,
        }}
        slotProps={{
          textField: {
            error: slotProps?.error,
          },
        }}
        onChange={handleDateChange}
        value={getDate(value)}
        onError={(error: DateValidationError | null) => {
          handleDateError?.(name, error !== null ? error : undefined);
        }}
        minDate={minDate}
        maxDate={maxDate}
        format="MMMM yyyy"
        openTo="month"
      />
      <CoreBox sx={{ marginLeft: '.75rem' }}>{showHelperText()}</CoreBox>
    </FormControl>
  );
};
